import {
  CAROUSEL_TARGET_PATIENT,
  getDefaultMapCarouselConfig,
  HEALTHDESK_DEFAULT_SLIDE_ROWS,
  normalizeCarouselTarget,
} from "./mapCarouselConstants";
import { isAllowedCarouselHref } from "./mapCarouselHref";
import { normalizeMergeSettings } from "./mapCarouselMerge";

/**
 * Normalize Firestore document into config for carousel + admin UI.
 * @param {FirebaseFirestore.DocumentData | null | undefined} data
 */
export const normalizeMapCarouselDocument = (data) => {
  const base = getDefaultMapCarouselConfig();
  if (!data || typeof data !== "object") {
    return { ...base, mergeMode: base.mergeMode, minPaidAds: base.minPaidAds };
  }

  const { mergeMode, minPaidAds } = normalizeMergeSettings(data);

  const rawSlides = Array.isArray(data.slides) ? data.slides : [];
  const slides = rawSlides
    .filter(
      (s) =>
        s &&
        typeof s.message === "string" &&
        s.message.trim() &&
        typeof s.buttonLabel === "string" &&
        s.buttonLabel.trim() &&
        typeof s.href === "string" &&
        isAllowedCarouselHref(s.href)
    )
    .map((s) => ({
      id: typeof s.id === "string" && s.id.trim() ? s.id.trim() : "",
      target: normalizeCarouselTarget(s.target),
      message: s.message.trim(),
      buttonLabel: s.buttonLabel.trim(),
      href: s.href.trim(),
      logoUrl:
        typeof s.logoUrl === "string" && s.logoUrl.trim() ? s.logoUrl.trim() : "",
      enabled: s.enabled !== false,
    }));

  return {
    mergeMode,
    minPaidAds,
    slides,
  };
};

/**
 * Effective global slide rows for the map: built-in defaults when Firestore has no usable slide rows
 * (missing doc, empty array, or every row disabled / invalid after normalize).
 * Rows are filtered to the viewer segment (Patient / Provider / Facility).
 * When that segment has no rows in the resolved list, falls back to built-in defaults for that
 * segment (e.g. Firestore has only provider slides but the viewer is a patient). If defaults have
 * nothing for the segment, falls back to patient rows from the list, then the full list.
 * @param {{ slides: Array<{ enabled?: boolean, target?: string }> }} normalized
 * @param {string} [viewerSegment]
 */
export function getEffectiveGlobalSlideRows(
  normalized,
  viewerSegment = CAROUSEL_TARGET_PATIENT
) {
  const rows = normalized.slides || [];
  let list;
  if (rows.length === 0) {
    list = HEALTHDESK_DEFAULT_SLIDE_ROWS;
  } else {
    list = rows.filter((s) => s.enabled !== false);
    if (list.length === 0) {
      list = HEALTHDESK_DEFAULT_SLIDE_ROWS;
    }
  }
  const seg = normalizeCarouselTarget(viewerSegment);
  const forSegment = list.filter((s) => normalizeCarouselTarget(s.target) === seg);
  if (forSegment.length > 0) {
    return forSegment;
  }
  const defaultsForSegment = HEALTHDESK_DEFAULT_SLIDE_ROWS.filter(
    (s) => normalizeCarouselTarget(s.target) === seg
  );
  if (defaultsForSegment.length > 0) {
    return defaultsForSegment;
  }
  const patientFromList = list.filter(
    (s) => normalizeCarouselTarget(s.target) === CAROUSEL_TARGET_PATIENT
  );
  if (patientFromList.length > 0) {
    return patientFromList;
  }
  return [...list];
}

/**
 * All enabled global rows (any target), preserving Firestore order. Used for anonymous
 * tri-target carousel ordering. Falls back to built-in defaults when the doc has rows but
 * none are enabled (otherwise CareMap shows no carousel).
 */
export function getAllEnabledGlobalSlideRows(normalized) {
  const rows = normalized.slides || [];
  if (rows.length === 0) {
    return [...HEALTHDESK_DEFAULT_SLIDE_ROWS];
  }
  const enabled = rows.filter((s) => s.enabled !== false);
  if (enabled.length === 0) {
    return [...HEALTHDESK_DEFAULT_SLIDE_ROWS];
  }
  return enabled;
}
